import { formatLean } from '../components/analyticsMap/choropleth'

const fmt = (n: number) => new Intl.NumberFormat('en-US').format(n)

export function formatPct(v: number): string {
  return `${Math.round(v * 100)}%`
}

export function formatSignedPp(v: number): string {
  return formatLean(v)
}

export const explainCopy = {
  doorsPerShift(doors: number, contactRatePct: number): string {
    return `At ${contactRatePct}% contact rate, canvassers knock approximately ${fmt(doors)} doors per shift. This is an editable assumption based on typical field conditions.`
  },

  contactRate(doors: number, contactRatePct: number, totalDoors: number): string {
    const contacts = Math.round((doors * contactRatePct) / 100)
    return `Knocking ${fmt(doors)} doors out of ${fmt(totalDoors)} total at ${contactRatePct}% contact rate yields ≈ ${fmt(contacts)} successful voter contacts. This assumes typical field success rates.`
  },

  capacityTotal(shifts: number, doors: number, contacts: number): string {
    return `Over ${fmt(shifts)} shifts, your capacity is ${fmt(doors)} doors and ≈ ${fmt(contacts)} voter contacts. This is computed from your canvasser availability and contact rate.`
  },

  turnoutLift(liftPp: number, bandLower: number, bandUpper: number, eligibleContacts: number | null, totalContacts: number): string {
    const base = `Each contacted voter with ${bandLower}–${bandUpper}% modeled vote propensity gains +${liftPp} pp turnout likelihood — an editable assumption (GOTV field-experiment literature), not a measurement.`
    if (eligibleContacts === null) {
      return `${base} Applies to the share of your ${fmt(totalContacts)} projected contacts whose modeled propensity falls in the ${bandLower}–${bandUpper}% band (exact count shown after you run).`
    }
    return `${base} Applies to ≈ ${fmt(eligibleContacts)} of your ${fmt(totalContacts)} projected contacts.`
  },

  persuasion(persuasionPp: number): string {
    return `Contacted voters in persuadable ranges gain +${persuasionPp} pp shift toward your candidate — an editable assumption derived from persuasion research, not a hard measurement. Effect varies by region and voter profile.`
  },

  advancedTurnoutDelta(): string {
    return `Advanced turnout strategies (early vote, mail, etc.) create additional turnout lift beyond standard GOTV contact, based on your program assumptions.`
  },

  winProbability(draws: number, winProbPct: number, leanFormatted: string | null): string {
    const leanNote = leanFormatted ? ` The race leans ${leanFormatted}, which shifts these odds.` : ''
    return `Across ${fmt(draws)} Monte Carlo simulations, your campaign wins in ${winProbPct}% of scenarios.${leanNote} This assumes your turnout and persuasion targets are met.`
  },

  marginBand(p10Pp: number, p90Pp: number): string {
    return `In 80% of scenarios, your margin lands between ${formatLean(p10Pp)} and ${formatLean(p90Pp)}. This is the likely outcome range given your field targets and the modeled uncertainty.`
  },

  /**
   * Votes-needed vs. votes-moved sentence. `needed` is the backend's `max(0, −baseline median
   * margin)`, so 0 means the baseline already wins — in that case we say so (with the baseline
   * cushion when `baselineMarginVotes` is known) instead of printing a negative "gap".
   * Vote counts are rounded to whole votes; a negative gap is never shown.
   */
  votesNeeded(needed: number, netMoved: number, baselineMarginVotes?: number | null): string {
    const n = (v: number) => fmt(Math.round(Math.abs(v)))
    const planClause = netMoved >= 0
      ? `your plan adds ≈ ${n(netMoved)} net votes of cushion`
      : `your plan gives back ≈ ${n(netMoved)} net votes of that cushion`
    if (needed <= 0) {
      const lead = baselineMarginVotes != null && baselineMarginVotes > 0
        ? `The baseline already wins by ≈ ${n(baselineMarginVotes)} votes`
        : `The baseline already wins`
      return `${lead}; ${planClause}.`
    }
    const shortfall = needed - netMoved
    if (shortfall <= 0) {
      return `You need ${n(needed)} votes to win. Your field plan moves ≈ ${n(netMoved)} net votes — enough to close the gap with ≈ ${n(shortfall)} to spare.`
    }
    return `You need ${n(needed)} votes to win. Your field plan moves ≈ ${n(netMoved)} net votes${netMoved < 0 ? ' the wrong way' : ''}, leaving ≈ ${n(shortfall)} votes still to find — your persuasion and turnout targets must bridge this.`
  },

  /** Model-card coverage detail: one verdict badge carries the state, this line carries the unit coverage. */
  modelCoverage(modeled: number, total: number): string {
    const excluded = Math.max(0, total - modeled)
    if (excluded === 0) return `All ${fmt(modeled)} precincts modeled`
    return `${fmt(modeled)} of ${fmt(total)} precincts modeled · ${fmt(excluded)} without usable results excluded`
  },

  netMovementNegative(): string {
    return `Untargeted GOTV in a district that leans against you turns out more opposing voters than yours, so net margin moves the wrong way. Target bluer precincts (see the sensitivity map) or add persuasion effort.`
  },

  netMovementPositive(): string {
    return `Your targeted turnout and persuasion plan nets more votes in your favor than it hands the opposition, so net margin moves your way. Keep contact focused on these precincts to protect the gain.`
  },

  smartFilterMode(): string {
    return `Build a target cohort inline from voter-file model scores — no saved list needed. Only the scores the simulator actually responds to are exposed here.`
  },

  turnoutPropensityBand(lo: number, hi: number): string {
    return `Keep voters whose modeled turnout propensity is ${lo}–${hi}. This is the movable band: voters below rarely vote no matter what, voters above already turn out, so GOTV contact moves neither — only the middle.`
  },

  yourSideLean(threshold: number): string {
    return `Keep voters whose modeled ideology is ${threshold} or higher (0 = conservative, 100 = progressive). This keeps the cohort's partisan weight on your side so turning them out adds net votes for you, not the opposition.`
  },

  persuasionMidBand(): string {
    return `Also limit to the persuadable middle (ideology 35–65) — the voters a persuasion conversation can actually move. Shown only when the persuasion lever is on.`
  },

  smartReachableCount(): string {
    return `How many voters in this district match your filter (committee-scoped, aggregate only). Counts below 10 show as "fewer than 10" to protect individual privacy.`
  },

  compareToUntargeted(): string {
    return `Runs your current smart-targeted cohort side by side with plain, untargeted whole-district GOTV, so you can see what targeting buys you: net margin shift, how many voters each reaches and contacts, and net votes per 1,000 contacts. Same capacity and assumptions — only the target universe changes.`
  },

  efficiencyNetPer1k(): string {
    return `Net votes moved per 1,000 contacts — the honest per-effort comparator. A targeted plan contacts far fewer voters, so its raw net understates it; efficiency shows the real gain for each unit of canvassing effort, letting you compare plans of different sizes fairly.`
  },

  presetGotvSweetSpot(): string {
    return `One click fills the smart filter to the GOTV sweet spot: the mid-propensity movable band (30–60) crossed with your-side lean (ideology ≥ 50). That combination turns out voters GOTV can actually move who also net votes for your side — the setup that flips net margin positive. Always available.`
  },

  presetWhereEffortHelps(): string {
    return `Switches targeting to the precincts where THIS scenario's marginal effort adds net votes — the positive-net precincts from your last run's sensitivity map. Because it reads the last run, you have to run the simulation once first.`
  },

  targetingBasisNote(): string {
    return `Targeting estimate from voter-file model scores — not tied to this race's returns.`
  },

  noPositivePrecinct(): string {
    return `No precinct adds net votes under untargeted GOTV — try persuasion or bluer targets.`
  },

  tornado(): string {
    return `Each bar is a precinct's net votes per +100 canvasser-shifts. Bars to the right add votes for you (send canvassers there); bars to the left are drags where untargeted GOTV turns out net opposition. This reconciles with the aggregate: many drags are why net can be negative.`
  },

  majorityProbability(draws: number, threshold: number, modeled: number, leanOnly: number): string {
    return `Across ${fmt(draws)} simulations, you hold a majority in ${threshold}% of scenarios. This assumes ${fmt(modeled)} seats shift your way via contact and persuasion, and ${fmt(leanOnly)} via lean alone. Success depends on field execution.`
  },

  allocation(totalShifts: number): string {
    return `Your canvassers are allocated across ${fmt(totalShifts)} shifts. Rebalance this allocation to focus capacity on high-priority turfs and voter segments.`
  },

  leverageIndex(): string {
    return `Leverage measures how efficiently your field plan moves the needle on the outcome — a ratio of your contact impact to your total contacts. Higher leverage targets fewer, higher-value voters.`
  },

  quadrantBadge(quadrantLabel: string): string {
    return `This voter lives in the ${quadrantLabel} quadrant: see the District Profile map to understand the neighborhood's lean and turnout patterns.`
  },

  dropoff(ratePct: number, fromYear: number, toLabel: string): string {
    return `${Math.round(ratePct)}% of ${fromYear} presidential voters skipped ${toLabel}. This drop-off band is your mobilize universe — people who vote in presidential years but not in this office's elections. Re-engage them before chasing new registrants.`
  },

  /**
   * Top-of-ticket estimate banner. Pass a concrete office noun (e.g. "US House") when known;
   * with no label the copy falls back to a generic "this race"/"this chamber" phrasing that
   * reads grammatically (the label is never spliced in as a pseudo-adjective).
   */
  topOfTicketBasis(officeLabel?: string | null, scope: 'race' | 'chamber' = 'race'): string {
    if (officeLabel) {
      return `Estimated from top-of-ticket partisanship & turnout — not ${officeLabel} returns. ` +
        `We don't have precinct-level ${officeLabel} results for this area, so outcome odds are hidden; ` +
        `the map still shows where your effort moves the most votes.`
    }
    // The chamber path renders a highest-leverage seat list (no sensitivity map) under topOfTicket.
    const tail = scope === 'chamber'
      ? `so outcome odds are hidden; the seat list below still shows where your effort helps most.`
      : `so outcome odds are hidden; the map still shows where your effort moves the most votes.`
    return `Estimated from top-of-ticket partisanship & turnout — not this ${scope}'s own returns. ` +
      `We don't have precinct-level results for this ${scope} in this area, ${tail}`
  },

  leanDefinition(): string {
    return `ORCL Lean compares this district to the nation, Cook-PVI style. D+ means it voted more Democratic than the country overall; R+ means more Republican. It's a partisan baseline built from presidential results — not a prediction for any single race.`
  },

  leanReconciliation(rawMarginFmt: string, nationalMarginFmt: string, leanFmt: string): string {
    return `The district's raw presidential result was ${rawMarginFmt}. Because the nation leaned ${nationalMarginFmt}, measured against the country the district is ${leanFmt}.`
  },

  raceKindToggle(): string {
    return `Switch between a general election (partisan D-vs-R modeling) and a party primary. Primary mode targets your primary electorate and estimates net votes moved toward your candidate — it does not forecast a winner, because we have no primary-return data to model one honestly.`
  },

  primaryMode(): string {
    return `Primary mode is a targeting tool, not a forecaster. It finds the primary voters worth reaching and estimates net votes moved toward your candidate; win-probability and margin are hidden because a Dem-vs-Dem outcome can't be modeled from general-election returns.`
  },

  primaryRegisteredDem(): string {
    return `Limit the cohort to registered Democrats — the closed-primary electorate. Turn this off for open or semi-open primary states where unaffiliated voters can pull a Democratic ballot.`
  },

  primaryTurnoutBand(lo: number, hi: number): string {
    return `Keep voters whose modeled Democratic-primary turnout propensity is ${lo}–${hi}. Primary turnout is its own model — far fewer, more-engaged voters than a general — so this is the band worth organizing.`
  },

  primaryLane(direction: 'progressive' | 'moderate', threshold: number): string {
    const side = direction === 'moderate' ? 'moderate' : 'progressive'
    const cmp = direction === 'moderate' ? 'at or below' : 'at or above'
    return `Your candidate runs on the ${side} side of the party, so keep voters whose modeled ideology is ${cmp} ${threshold} (0 = conservative, 100 = progressive). This aligns the cohort's net-vote weight with your candidate; same-party voters in the opposite lane count against you when turned out.`
  },

  primaryIssue(issueLabel: string): string {
    return `Keep voters Catalist models as likely or very likely to support the progressive position on ${issueLabel}. Note: this score is derived from Catalist's Ideology Plus model (calibrated per issue), so it narrows to ideologically-aligned voters — it won't surface someone conservative overall but progressive on this one issue. It filters WHO you contact; it doesn't change the net-vote weight (that stays on the ideology lane).`
  },

  primaryActivismFloor(floor: number): string {
    return `Keep only voters whose modeled general-activism score is ${floor} or higher — the engaged partisans who over-turn-out in low-turnout primaries.`
  },

  primaryNetMovement(): string {
    return `Net votes your plan moves toward your candidate among the primary electorate: turning out lane-aligned voters adds votes, turning out opponent-lane voters subtracts them, and persuasion moves the middle. No winner is projected — this is targeting impact, not a forecast.`
  },
}
